/**
 * Centralized scoring utilities for Wright Picks
 * 
 * SCORING FORMULA: Points = Team Seed × Round Multiplier
 * 
 * Example: A 6 seed winning Elite 8 (2x multiplier) = 6 × 2 = 12 points
 * This rewards picking upsets - higher seeds are worth more points!
 */

import { supabase } from "../supabaseClient"

export interface ScoringConfig {
  round: number
  round_name: string
  multiplier: number
}

// Cache for scoring config to avoid repeated DB calls
let scoringConfigCache: { year: number; configs: ScoringConfig[] } | null = null

/**
 * Fetch scoring configuration for a given year from the database
 */
export async function getScoringConfig(year: number): Promise<ScoringConfig[]> {
  // Return cached config if it's for the same year
  if (scoringConfigCache?.year === year) {
    return scoringConfigCache.configs
  }

  const { data, error } = await supabase
    .from('scoring_config')
    .select('round, round_name, multiplier')
    .eq('year', year)
    .order('round')

  if (error || !data || data.length === 0) {
    // Return defaults if no config found
    return getDefaultScoringConfig()
  }

  // Cache the result
  scoringConfigCache = { year, configs: data }
  return data
}

/**
 * Get default scoring configuration (used when DB config not available)
 */
export function getDefaultScoringConfig(): ScoringConfig[] {
  return [
    { round: 1, round_name: 'Round of 64', multiplier: 1 },
    { round: 2, round_name: 'Round of 32', multiplier: 1 },
    { round: 3, round_name: 'Sweet 16', multiplier: 2 },
    { round: 4, round_name: 'Elite 8', multiplier: 2 },
    { round: 5, round_name: 'Final Four', multiplier: 3 },
    { round: 6, round_name: 'Championship', multiplier: 3 },
  ]
}

/**
 * Clear the scoring config cache (call when config is updated)
 */
export function clearScoringConfigCache(): void {
  scoringConfigCache = null
}

/**
 * Calculate points for a single team based on their seed and wins
 * 
 * Formula: For each win, points = seed × multiplier for that round
 * 
 * @param seed - The team's tournament seed (1-16)
 * @param wins - Number of wins the team has
 * @param configs - Scoring config from the database
 */
export function calculateTeamPointsWithConfig(
  seed: number, 
  wins: number, 
  configs: ScoringConfig[]
): number {
  if (wins <= 0 || seed <= 0) return 0
  
  let totalPoints = 0
  for (let i = 0; i < wins && i < configs.length; i++) {
    totalPoints += seed * configs[i].multiplier
  }
  return totalPoints
}

/**
 * Calculate points for a single team using default config
 * Convenience function for backwards compatibility
 */
export function calculateTeamPoints(seed: number, wins: number): number {
  return calculateTeamPointsWithConfig(seed, wins, getDefaultScoringConfig())
}

/**
 * Calculate total points for an entry based on their picked teams
 */
export function calculateEntryPoints(teams: { seed: number; wins: number }[]): number {
  const configs = getDefaultScoringConfig()
  return teams.reduce((total, team) => 
    total + calculateTeamPointsWithConfig(team.seed || 1, team.wins || 0, configs), 0
  )
}

/**
 * Calculate total points for an entry using specific config
 */
export function calculateEntryPointsWithConfig(
  teams: { seed: number; wins: number }[], 
  configs: ScoringConfig[]
): number {
  return teams.reduce((total, team) => 
    total + calculateTeamPointsWithConfig(team.seed || 1, team.wins || 0, configs), 0
  )
}

/**
 * Count teams still alive (not eliminated)
 */
export function countTeamsAlive(teams: { is_eliminated: boolean }[]): number {
  return teams.filter(t => !t.is_eliminated).length
}

/**
 * Calculate max potential points for an entry
 * = current points + remaining potential for alive teams
 * 
 * @param teams - Array of teams with seed, wins, and elimination status
 * @param configs - Scoring config (defaults to standard config)
 * @returns Object with current points, max possible points, and remaining potential
 */
export function calculateMaxPotential(
  teams: { seed: number; wins: number; is_eliminated: boolean }[],
  configs: ScoringConfig[] = getDefaultScoringConfig()
): { current: number; max: number; potential: number } {
  let current = 0
  let potential = 0
  
  teams.forEach(team => {
    // Current points from wins so far
    current += calculateTeamPointsWithConfig(team.seed, team.wins, configs)
    
    // Remaining potential for alive teams
    if (!team.is_eliminated) {
      for (let round = team.wins; round < configs.length; round++) {
        potential += team.seed * configs[round].multiplier
      }
    }
  })
  
  return { current, max: current + potential, potential }
}

/**
 * Calculate maximum possible points for a team that wins all 6 games
 * This depends on the seed - higher seeds have higher max points
 */
export function calculateMaxPointsForSeed(seed: number, configs: ScoringConfig[]): number {
  return configs.reduce((sum, c) => sum + (seed * c.multiplier), 0)
}

/**
 * Calculate theoretical max points if you picked all 16 seeds as champions
 * (Not realistic, but useful for context)
 */
export function calculateTheoreticalMax(configs: ScoringConfig[]): number {
  // Sum of multipliers × average seed value (not really useful, but here for reference)
  const multiplierSum = configs.reduce((sum, c) => sum + c.multiplier, 0)
  return multiplierSum * 16 // Max would be if a 16 seed won it all
}

/**
 * Calculate payout amounts based on total pot and percentages
 */
export function calculatePayouts(
  totalPot: number,
  percentages: { place: number; percentage: number }[]
): { place: number; percentage: number; amount: number }[] {
  return percentages.map(p => ({
    ...p,
    amount: Math.round((p.percentage / 100) * totalPot)
  }))
}

/**
 * Format place as ordinal (1st, 2nd, 3rd, etc.)
 */
export function formatPlace(place: number): string {
  if (place === 1) return '1st'
  if (place === 2) return '2nd'
  if (place === 3) return '3rd'
  return `${place}th`
}
